import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Clock, CheckCircle2, AlertCircle, Eye } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { type AgentExecution } from '@/hooks/use-agents'

interface AgentExecutionHistoryProps {
  executions: AgentExecution[]
  onViewResult: (execution: AgentExecution) => void
  isLoading?: boolean
}

const getAgentIcon = (agentName: string): string => {
  const icons: Record<string, string> = {
    comparator: '⚖️',
    risk_detector: '🎯',
    evaluator: '📊'
  }
  return icons[agentName] || '🤖'
}

const getAgentDisplayName = (agentName: string): string => {
  const names: Record<string, string> = {
    comparator: 'Comparador',
    risk_detector: 'Detector de Riesgos',
    evaluator: 'Evaluador'
  }
  return names[agentName] || agentName
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'failed':
      return <AlertCircle className="h-4 w-4 text-red-600" />
    case 'running':
      return <Clock className="h-4 w-4 text-blue-600 animate-spin" />
    default:
      return null
  }
}

const getStatusBadge = (status: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    completed: 'default',
    failed: 'destructive',
    running: 'secondary'
  }
  const labels: Record<string, string> = {
    completed: 'Completado',
    failed: 'Fallido',
    running: 'En proceso'
  }
  return (
    <Badge variant={variants[status] || 'outline'} className="text-xs">
      {labels[status] || status}
    </Badge>
  )
}

const getConfidenceBadge = (confidence?: number) => {
  if (confidence === undefined) return null

  const percentage = Math.round(confidence * 100)
  let variant: 'default' | 'secondary' | 'destructive' = 'default'

  if (percentage >= 70) {
    variant = 'default' // Verde
  } else if (percentage >= 40) {
    variant = 'secondary' // Amarillo
  } else {
    variant = 'destructive' // Rojo
  }

  return (
    <Badge variant={variant} className="text-xs ml-2">
      {percentage}% confianza
    </Badge>
  )
}

export default function AgentExecutionHistory({
  executions,
  onViewResult,
  isLoading = false
}: AgentExecutionHistoryProps) {
  if (isLoading) {
    return (
      <Card className="w-full h-full">
        <CardHeader>
          <CardTitle className="text-lg">Historial de Ejecuciones</CardTitle>
          <CardDescription>Cargando historial...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2 pb-3 border-b border-gray-100 last:border-0">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-7 w-full" />
            </div>
          ))}
        </CardContent>
      </Card>
    )
  }

  if (!executions || executions.length === 0) {
    return (
      <Card className="w-full h-full">
        <CardHeader>
          <CardTitle className="text-lg">Historial de Ejecuciones</CardTitle>
          <CardDescription>No hay ejecuciones previas</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground">
              Ejecuta un agente para ver el historial aquí
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle className="text-lg">Historial de Ejecuciones</CardTitle>
        <CardDescription>
          {executions.length} ejecución{executions.length !== 1 ? 'es' : ''} total{executions.length !== 1 ? 'es' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[600px] px-6">
          <div className="space-y-4 pb-4">
            {executions.map((execution, index) => (
              <div key={execution.id}>
                <div className="flex items-start gap-4 py-3">
                  <div className="flex-shrink-0 text-2xl mt-1">
                    {getAgentIcon(execution.agent_name)}
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-sm font-semibold">
                          {execution.agent_display_name || getAgentDisplayName(execution.agent_name)}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(execution.created_at), {
                              addSuffix: true,
                              locale: es
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {getStatusIcon(execution.status || 'completed')}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(execution.status || 'completed')}
                      {getConfidenceBadge(execution.confidence_score ?? execution.confidence)}
                    </div>

                    {execution.error && (
                      <p className="text-xs text-red-600 bg-red-50 p-2 rounded">
                        {execution.error}
                      </p>
                    )}

                    {execution.result && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => onViewResult(execution)}
                      >
                        <Eye className="h-3 w-3 mr-2" />
                        Ver resultado
                      </Button>
                    )}
                  </div>
                </div>

                {index < executions.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  )
}
